import { useContext, useEffect, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../../components/Common/LanguageToggle";
import { env, LANDING_URL } from "../../env";
import { Controller, useForm } from "react-hook-form";
import AuthLayout from "../../components/Auth/AuthLayout";
import AccountStatusModal from "../../components/Auth/AccountStatusModal";
import Button from "../../components/Common/Button";
import ErrorMessage from "../../components/Common/ErrorMessage";
import PasswordInput from "../../components/Common/PasswordInput";
import { getInfluencerProfile, identityLogin, influencerLogin, verifyTurnstile } from "../../api/authApi";
import { buildLoginPayload } from "../../api/payload";
import { extractLoginTokens } from "../../api/response";
import { clearAuthData } from "../../api/apiService";
import { AuthContext } from "../../context";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";
import { showErrorToast, showSuccessToast } from "../../utils/toast";

const Login = () => {
    const { t } = useTranslation();
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const { run, isLoading, clearError, setError } = useAsyncAction();
    const [statusModal, setStatusModal] = useState(null);
    const [turnstileToken, setTurnstileToken] = useState(null);
    const [turnstileVerified, setTurnstileVerified] = useState(false);

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            email: "",
            password: "",
        },
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            const emailInput = document.querySelector("input[type='email']");
            const passwordInput = document.querySelector("input[type='password']");
            if (emailInput?.value) setValue("email", emailInput.value.trim(), { shouldValidate: true });
            if (passwordInput?.value) setValue("password", passwordInput.value, { shouldValidate: true });
        }, 500);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async (values) => {
        clearError();
        const loginPayload = buildLoginPayload({ email: values.email, password: values.password, turnstileToken });
        const firstLoginResult = await run(
            () => influencerLogin(loginPayload),
            { errorFallback: t("AUTH_LOGIN_ERROR_FIRST_STEP") }
        );
        if (!firstLoginResult) return;
        if (firstLoginResult?.success === false) {
            const message = firstLoginResult?.error || t("AUTH_LOGIN_ERROR_INVALID_CREDENTIALS");
            setError({ message });
            showErrorToast(message);
            return;
        }

        const rpcToken = extractLoginTokens({ rpcResult: firstLoginResult, identityResult: null }).rpcToken;
        if (!rpcToken) {
            const status = firstLoginResult?.Status || firstLoginResult?.status || "";
            const reason = firstLoginResult?.Reason || firstLoginResult?.reason || "";
            const statusLower = status.toLowerCase();
            if (statusLower === "pending" || statusLower === "rejected" || statusLower === "inactive") {
                setStatusModal({ status, reason });
            } else {
                const message = t("AUTH_LOGIN_ERROR_NO_RPC_TOKEN");
                setError({ message });
                showErrorToast(message);
            }
            return;
        }

        const identityResult = await run(
            () => identityLogin(loginPayload),
            { errorFallback: t("AUTH_LOGIN_ERROR_API_LOGIN") }
        );
        if (!identityResult) return;

        const bearerToken = extractLoginTokens({ rpcResult: null, identityResult }).bearerToken;
        if (!bearerToken) {
            const message = t("AUTH_LOGIN_ERROR_NO_BEARER_TOKEN");
            setError({ message });
            showErrorToast(message);
            return;
        }

        // Store tokens so the RPC interceptor can attach them for the profile call
        authContext.login({ Email: values.email }, bearerToken, true, rpcToken, true);

        const profile = await run(
            () => getInfluencerProfile(values.email),
            { errorFallback: t("AUTH_LOGIN_ERROR_PROFILE_FETCH") }
        );
        if (!profile) {
            clearAuthData(); // roll back stored tokens — login is incomplete
            return;
        }

        authContext.setAuthUser(profile);
        navigate("/dashboard");
        showSuccessToast(t("AUTH_LOGIN_SUCCESS_SIGNED_IN"));
    };

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors);
    };

    return (
        <>
        <div className="fixed top-6 right-6 z-50">
            <LanguageToggle />
        </div>
        <AccountStatusModal
            isOpen={Boolean(statusModal)}
            status={statusModal?.status}
            reason={statusModal?.reason}
            onClose={() => setStatusModal(null)}
        />
        <AuthLayout
            left={(
                <div className="order-2 flex flex-col justify-center px-6 py-10 sm:px-10 lg:order-1 lg:px-16">
                    <div className="flex items-center gap-3 text-sm font-semibold tracking-[0.2em] text-slate-700">
                        <a href={LANDING_URL}>
                            <img
                                src="/assets/icons/korres-shares-logo.png"
                                alt={t("AUTH_LOGIN_ALT_KORRES_SHARES")}
                                className="h-[4.5rem] w-[4.5rem] hover:opacity-75 transition-opacity"
                            />
                        </a>
                    </div>

                    <div className="mt-10 max-w-md">
                            <h1 className="text-2xl font-semibold text-slate-900">
                                {t("AUTH_LOGIN_TITLE")}
                            </h1>
                            <p className="mt-2 text-sm text-slate-500">
                                {t("AUTH_TAGLINE")}
                            </p>
                        <form className="mt-8 space-y-4" onSubmit={handleSubmit(onSubmit, handleFormInvalid)}>
                            <div>
                                <Controller
                                    name="email"
                                    control={control}
                                    rules={{
                                        required: t("AUTH_VALIDATION_EMAIL_REQUIRED"),
                                        pattern: {
                                            value: /\S+@\S+\.\S+/,
                                            message: t("AUTH_VALIDATION_EMAIL_INVALID"),
                                        },
                                    }}
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            onKeyDown={(e) => e.key === " " && e.preventDefault()}
                                            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ""))}
                                            onBlur={(e) => { field.onBlur(); field.onChange(e.target.value.replace(/\s/g, "")); }}
                                            type="email"
                                            placeholder={t("AUTH_PLACEHOLDER_EMAIL")}
                                            className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none transition focus:border-primary"
                                        />
                                    )}
                                />
                                <ErrorMessage message={errors.email?.message} />
                            </div>
                            <div>
                                <Controller
                                    name="password"
                                    control={control}
                                    rules={{ required: t("AUTH_VALIDATION_PASSWORD_REQUIRED") }}
                                    render={({ field }) => (
                                        <PasswordInput
                                            {...field}
                                            placeholder={t("AUTH_PLACEHOLDER_PASSWORD")}
                                        />
                                    )}
                                />
                                <ErrorMessage message={errors.password?.message} />
                            </div>
                            <div>
                                <Link
                                    to="/forgot-password"
                                    className="text-xs font-semibold text-slate-500 underline underline-offset-4 hover:opacity-75"
                                >
                                    {t("AUTH_LOGIN_FORGOT_PASSWORD")}
                                </Link>
                            </div>
                            <Turnstile
                                siteKey={env.REACT_APP_TURNSTILE_SITE_KEY}
                                onSuccess={async (token) => {
                                    setTurnstileToken(token);
                                    const verified = await verifyTurnstile(token);
                                    setTurnstileVerified(verified);
                                }}
                                onExpire={() => { setTurnstileToken(null); setTurnstileVerified(false); }}
                                onError={() => { setTurnstileToken(null); setTurnstileVerified(false); }}
                                options={{ theme: "light", size: "flexible" }}
                            />
                            <Button
                                type="submit"
                                disabled={isLoading || !turnstileVerified}
                                className="mt-4 w-full"
                            >
                                {isLoading ? t("AUTH_LOGIN_SIGNING_IN") : t("AUTH_LOGIN_SIGN_IN")}
                            </Button>
                        </form>

                        <div className="mt-8 text-xs text-slate-500">
                            {t("AUTH_LOGIN_NO_ACCOUNT")}{" "}
                            <span className="font-semibold text-primary opacity-40 cursor-default">
                                {t("AUTH_LOGIN_SUBMIT_APPLICATION")}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            right={(
                <div className="relative order-1 w-full overflow-hidden bg-cream lg:order-2 lg:h-screen">
                    <img
                        src="/assets/images/common/login.png"
                        alt=""
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
        />
        <span className="fixed bottom-3 right-4 text-xs text-slate-400">
            v{env.REACT_APP_VERSION}
        </span>
        </>
    );
};

export default Login;
